import { CommonModule, KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { ExtendedMap, Plugin, PluginMap, WidgetParams } from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { store } from '../../jmkw.store';
import { WidgetWrapperComponent } from './widget-wrapper/widget-wrapper.component';
import { JmkwApiService } from '../../jmkw-api.service';
import type { ResourceHandle } from "@arcgis/core/core/Handles";

@Component({
  selector: 'jmkw-widget-bar',
  standalone: true,
  imports: [CommonModule, WidgetWrapperComponent],
  templateUrl: './widget-bar.component.html',
  styleUrl: './widget-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WidgetBarComponent implements OnInit, OnDestroy {
  @Input() activeWidgetMap?: ExtendedMap<WidgetParams>;

  private readonly _logger = inject(NGXLogger);
  private readonly _api = inject(JmkwApiService);
  private _mapVisibilityHandle?: ResourceHandle;

  title = 'Widgets';

  ngOnInit(): void {
    this._setupMapVisibilityTracking();
  }

  ngOnDestroy(): void {
    this._mapVisibilityHandle?.remove();
  }

  /**
   * Sets up tracking of map layer visibility changes
   */
  private _setupMapVisibilityTracking(): void {
    this._mapVisibilityHandle = this._trackMapVisibilityChanges();
  }

  /**
   * Tracks the visibility changes of the map layers and updates the visible layers accordingly.
   * Uses the map as the source of truth. Tracks changes to the visible field and sets
   * visibleLayers to a set of the visible layer ids.
   */
  private _trackMapVisibilityChanges(): ResourceHandle {
    return reactiveUtils.watch(
      () => {
        const map = this._api.getMap();
        if (!map?.layers) {
          return [];
        }

        return map.layers.reduce<string[]>(
          (acc, layer) => (layer?.visible ? [...acc, layer.id] : acc),
          []
        );
      },
      (visibleLayerIDs) => {
        if (!visibleLayerIDs.length) {
          return;
        }

        const pluginMap = store.pluginMap();
        if (!pluginMap) {
          this._logger.warn('[WidgetBarComponent] Plugin map is not available');
          return;
        }

        const params = this._filterPluginMapByWidgets(
          pluginMap,
          new Set(visibleLayerIDs)
        );

        const allWidgets = pluginMap.allWidgets();
        if (!allWidgets) {
          return;
        }

        const visibleLayerWidgets = allWidgets.filter(
          (value: WidgetParams, key: string) => {
            return params.has(key);
          }
        );

        const currentActiveWidgets = store.activeWidgetMap?.();
        const newWidgets = currentActiveWidgets?.size
          ? currentActiveWidgets.concat(visibleLayerWidgets)
          : visibleLayerWidgets;

        store.activeWidgetMap.set(newWidgets);
      }
    );
  }

  /**
   * Filters the plugin map by visible widgets
   * @param pluginMap The plugin map to filter
   * @param visibleLayerIDs Set of visible layer IDs
   * @returns Set of widget IDs that should be visible
   */
  private _filterPluginMapByWidgets(
    pluginMap: PluginMap,
    visibleLayerIDs: Set<string>
  ): Set<string> {
    if (!pluginMap || !visibleLayerIDs.size) {
      return new Set<string>();
    }

    const resultSet = new Set<string>();

    const traverse = (id: string, plugin: Plugin): boolean => {
      if (!id || !plugin) {
        return false;
      }

      let pluginIdFound = visibleLayerIDs.has(id);

      // recursively check children ids
      if (plugin.children?.size) {
        for (const [childId, childPlugin] of plugin.children) {
          if (traverse(childId, childPlugin)) {
            pluginIdFound = true;
          }
        }
      }

      // if the id or a child id exists AND this one has a widget, add it
      if (pluginIdFound && plugin.widget) {
        resultSet.add(id);
      }

      return pluginIdFound;
    };

    for (const [id, plugin] of pluginMap) {
      traverse(id, plugin);
    }

    return resultSet;
  }

  /**
   * Sorts widgets by their order property
   */
  public sort = (
    a: KeyValue<string, WidgetParams>,
    b: KeyValue<string, WidgetParams>
  ): number => {
    // Early return if either value is null/undefined
    if (!a?.value || !b?.value) {
      return 0;
    }

    const orderA = a.value.order ?? 0;
    const orderB = b.value.order ?? 0;

    if (orderA > orderB) return 1;
    if (orderA < orderB) return -1;
    return 0;
  };
}
